import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Group,
  Button,
  Select,
  Switch,
  Text,
  Divider,
  Grid,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { tasksApi } from '../../../services/api/tasks';
import { usersApi, type InternalUser } from '../../../services/api/users';
import { taskPhasesApi } from '../../../services/api/taskPhases';
import { taskSubGroupsApi } from '../../../services/api/taskSubGroups';
import {
  TASK_PRIORITY_OPTIONS,
} from '../../../config/taskConfig';
import type {
  TaskWithDetails,
  TaskPriority,
  TaskPhase,
  TaskPhaseRecord,
  TaskSubGroupWithDetails,
} from '../../../types';
import { parseDateOnly, toISODateString, toDateValue } from '../../../utils/dateHelpers';

interface TaskModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  task?: TaskWithDetails | null;
  /** MED-162: Default phase to auto-select when creating a new task from within a phase */
  defaultPhase?: TaskPhase | null;
  /** MED-114: Pre-populate title when creating from health alerts */
  defaultTitle?: string;
  /** MED-114: Pre-populate description when creating from health alerts */
  defaultDescription?: string;
  onSaved: () => void;
  existingTasks?: TaskWithDetails[];
}

export function TaskModal({
  opened,
  onClose,
  cohortId,
  task,
  defaultPhase,
  defaultTitle,
  defaultDescription,
  onSaved,
  existingTasks = [],
}: TaskModalProps) {
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [phases, setPhases] = useState<TaskPhaseRecord[]>([]);
  const [subGroups, setSubGroups] = useState<TaskSubGroupWithDetails[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPhases, setLoadingPhases] = useState(false);
  const isEditing = !!task;

  const form = useForm({
    initialValues: {
      isPerSession: false,
      phaseId: null as string | null,
      subGroupId: null as string | null,
      title: '',
      description: '',
      assigneeId: null as string | null,
      parentTaskId: null as string | null,
      dueDate: null as Date | null,
      priority: 'medium' as TaskPriority,
      isRequired: true,
    },
  });

  // Watch phase selection to filter sub-groups
  const selectedPhaseId = form.values.phaseId;

  // Fetch users for assignee dropdown
  useEffect(() => {
    if (opened && users.length === 0) {
      setLoadingUsers(true);
      usersApi.listInternal()
        .then(setUsers)
        .catch(console.error)
        .finally(() => setLoadingUsers(false));
    }
  }, [opened, users.length]);

  // Fetch phases
  useEffect(() => {
    if (opened && phases.length === 0) {
      setLoadingPhases(true);
      taskPhasesApi.getAll()
        .then(setPhases)
        .catch(console.error)
        .finally(() => setLoadingPhases(false));
    }
  }, [opened, phases.length]);

  // Fetch sub-groups
  useEffect(() => {
    if (opened) {
      taskSubGroupsApi.getAll()
        .then(setSubGroups)
        .catch(console.error);
    }
  }, [opened]);

  // Filter sub-groups by selected phase
  const filteredSubGroups = selectedPhaseId
    ? subGroups.filter(sg => sg.phase_id === selectedPhaseId)
    : [];

  // Reset form when modal opens
  // MED-162: Auto-select phase when defaultPhase is provided (creating from within a phase)
  useEffect(() => {
    if (opened) {
      if (task) {
        // Editing existing task - find phase by slug/name match
        const matchedPhase = phases.find(p => p.slug === task.phase);
        form.setValues({
          isPerSession: false,
          phaseId: matchedPhase?.id || null,
          subGroupId: null,
          title: task.title,
          description: task.description || '',
          assigneeId: task.assignee_id,
          parentTaskId: task.parent_task_id || null,
          dueDate: parseDateOnly(task.due_date),
          priority: task.priority,
          isRequired: true,
        });
      } else {
        // Creating new task - check for defaultPhase
        const matchedDefaultPhase = defaultPhase
          ? phases.find(p => p.slug === defaultPhase)
          : null;
        form.setValues({
          isPerSession: false,
          phaseId: matchedDefaultPhase?.id || null,
          subGroupId: null,
          title: defaultTitle || '',
          description: defaultDescription || '',
          assigneeId: null,
          parentTaskId: null,
          dueDate: null,
          priority: 'medium',
          isRequired: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, task, phases, defaultPhase]);

  // Format users for Select component
  const userOptions = users.map(user => ({
    value: user.id,
    label: user.name,
  }));

  // Format phases for Select component
  const phaseOptions = phases
    .filter(p => p.is_active && !p.is_archived)
    .map(phase => ({
      value: phase.id,
      label: phase.name,
    }));

  // Format sub-groups for Select component
  const subGroupOptions = filteredSubGroups
    .filter(sg => sg.is_active && !sg.is_archived)
    .map(sg => ({
      value: sg.id,
      label: sg.name,
    }));

  // Format parent tasks for Select component
  const parentTaskOptions = existingTasks
    .filter(t => !t.parent_task_id && t.id !== task?.id) // Only top-level tasks, exclude current task
    .map(t => ({
      value: t.id,
      label: t.title,
    }));

  const handleSave = async () => {
    const values = form.values;

    // Validate
    if (!values.title.trim()) {
      form.setFieldError('title', 'Title is required');
      return;
    }

    if (!values.phaseId) {
      form.setFieldError('phaseId', 'Phase is required');
      return;
    }

    if (!values.subGroupId) {
      form.setFieldError('subGroupId', 'Sub-Group is required');
      return;
    }

    try {
      setSaving(true);

      // Find phase slug for API (backend uses enum)
      const selectedPhase = phases.find(p => p.id === values.phaseId);

      const data = {
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        priority: values.priority,
        phase: selectedPhase?.slug as 'cohort_setup' | 'cohort_prep' | 'live_delivery' | 'wrap_renewal' | undefined,
        sub_group: values.subGroupId ? subGroups.find(sg => sg.id === values.subGroupId)?.name : undefined,
        assignee_id: values.assigneeId || undefined,
        parent_task_id: values.parentTaskId || undefined,
        due_date: toISODateString(values.dueDate),
      };

      if (isEditing && task) {
        await tasksApi.updateTask(cohortId, task.id, data);
        notifications.show({
          title: 'Success',
          message: 'Task updated successfully',
          color: 'green',
        });
      } else {
        await tasksApi.createTask(cohortId, data);
        notifications.show({
          title: 'Success',
          message: 'Task created successfully',
          color: 'green',
        });
      }
      onSaved();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : `Failed to ${isEditing ? 'update' : 'create'} task`,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">{isEditing ? 'Edit Task' : 'Add Task'}</Text>}
      size="lg"
    >
      <Stack gap="md">
        {/* Per-Session Task Toggle - only for new tasks */}
        {!isEditing && (
          <Switch
            label="Per-Session Task"
            description="Creates a separate task for each session in the cohort"
            checked={form.values.isPerSession}
            onChange={(event) => form.setFieldValue('isPerSession', event.currentTarget.checked)}
          />
        )}

        {/* Phase and Sub-Group */}
        <Grid>
          <Grid.Col span={6}>
            <Select
              label="Phase"
              description="Which phase this task belongs to"
              placeholder="Select phase"
              data={phaseOptions}
              required
              disabled={loadingPhases}
              value={form.values.phaseId}
              onChange={(value) => {
                form.setFieldValue('phaseId', value);
                form.setFieldValue('subGroupId', null); // Reset sub-group when phase changes
              }}
              error={form.errors.phaseId}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Sub-Group"
              description="Grouping within the phase"
              placeholder="Select sub-group"
              data={subGroupOptions}
              disabled={!selectedPhaseId || filteredSubGroups.length === 0}
              required
              value={form.values.subGroupId}
              onChange={(value) => form.setFieldValue('subGroupId', value)}
              error={form.errors.subGroupId}
            />
          </Grid.Col>
        </Grid>

        {/* Task Title */}
        <TextInput
          label="Task Title"
          placeholder="Enter task title"
          required
          {...form.getInputProps('title')}
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Task description (optional)"
          minRows={2}
          {...form.getInputProps('description')}
        />

        {/* Assignee */}
        <Select
          label="Assignee"
          placeholder="Select assignee"
          data={userOptions}
          clearable
          searchable
          disabled={loadingUsers}
          value={form.values.assigneeId}
          onChange={(value) => form.setFieldValue('assigneeId', value)}
        />

        {/* Parent Task */}
        <Select
          label="Parent Task"
          description="Make this a subtask of another task"
          placeholder="None (standalone task)"
          data={parentTaskOptions}
          clearable
          searchable
          disabled={parentTaskOptions.length === 0}
          value={form.values.parentTaskId}
          onChange={(value) => form.setFieldValue('parentTaskId', value)}
        />

        {/* Due Date Settings */}
        <Divider label="Due Date Settings" labelPosition="left" />

        {/* Due Date */}
        <DateInput
          label="Due Date"
          placeholder="Select date"
          valueFormat="MM/DD/YYYY"
          clearable
          value={form.values.dueDate}
          onChange={(value) => form.setFieldValue('dueDate', toDateValue(value))}
        />

        {/* Other Settings */}
        <Divider label="Other Settings" labelPosition="left" />

        <Grid>
          <Grid.Col span={6}>
            <Select
              label="Priority"
              data={TASK_PRIORITY_OPTIONS}
              required
              value={form.values.priority}
              onChange={(value) => form.setFieldValue('priority', value as TaskPriority)}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Select
              label="Required"
              data={[
                { value: 'true', label: 'Yes - Required' },
                { value: 'false', label: 'No - Optional' },
              ]}
              value={form.values.isRequired ? 'true' : 'false'}
              onChange={(value) => form.setFieldValue('isRequired', value === 'true')}
            />
          </Grid.Col>
        </Grid>

        {/* Footer */}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            {isEditing ? 'Save Changes' : 'Add Task'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
